/* eslint-disable import/no-unresolved */
import { format } from 'date-fns';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { usePDF } from 'react-to-pdf';
// @ts-ignore
import {
  Box,
  Button,
  Message,
} from '@zolteam/zol-admin-ui';
import { useParams, useSearchParams } from 'react-router-dom';
// Assets
import Adult from '../../assets/icons/adult.svg?react';
import Animal from '../../assets/icons/animal.svg?react';
import ChevronLeft from '../../assets/icons/chevron-left.svg?react';
import ChevronRight from '../../assets/icons/chevron-right.svg?react';
import Children from '../../assets/icons/children.svg?react';
import Clock from '../../assets/icons/clock.svg?react';
import Infant from '../../assets/icons/infant.svg?react';
import Itinerary from '../../assets/icons/itinerary.svg?react';
import Location from '../../assets/icons/location.svg?react';
import Luggage from '../../assets/icons/luggage.svg?react';
import Phone from '../../assets/icons/phone.svg?react';
import LogoFull from '../../assets/logo-full-white.svg?react';
import RecapPicture from '../../assets/recap.svg?react';
// Components
import FlightBriefPDF from '../../components/templates/SharedFlightBriefPDF/FlightBriefPDF';
// Store
// Utils
import { isIOSDevice, isMobileDevice } from '../../utils/deviceDetection';
// Styles
import styles from './SharedFlightBrief.module.css';
// service
import { daysBetweenDepartureAndArrival } from '../../domain/bookings/bookingsUseCases';
import {
  capitalizedValue,
  formatBookingDateLong,
  formatTerminalOpeningHours,
  formatTimeString,
  getLuggageTotalCount,
  getPassengersTotalCount,
} from '../../domain/flightBrief/flightBriefUseCases';
import { useGetSharedFlightBrief } from '../../services/flightBriefs/flightBriefsQueries';
import { useAppContext } from '../../store/AppContext';

/**
 * Shared Flight Brief View
 * @returns {JSX.Element}
 */
function SharedFlightBrief() {
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const locale = searchParams.get('lng') || 'fr';
  const { t } = useTranslation(undefined, { lng: locale });
  const [appState] = useAppContext();

  // Check if we're in download mode via query parameter
  const isDownloadMode = searchParams.has('download');

  // Check if token is passed as query parameter
  const token = searchParams.get('token');

  // Check if user can download: either authenticated admin OR has a valid token
  const canDownload = (appState?.auth?.token) || token;

  // PDF and mobile detection states
  const [downloadAttempted, setDownloadAttempted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const [selectedLocation, setSelectedLocation] = useState(/** @type {'departure' | 'arrival'} */('departure'));
  const autoDownloadStartedRef = useRef(false);
  const carouselRef = useRef(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const {
    data: flightBrief,
    isError,
    error,
    isLoading,
  } = useGetSharedFlightBrief(uuid);

  // Generate filename for PDF
  const departureCode = flightBrief?.departure?.airport?.airportCode?.toUpperCase();
  const departureDate = flightBrief?.departure?.date ? format(new Date(flightBrief?.departure?.date), 'dd-MM-yyyy') : '';
  const arrivalCode = flightBrief?.arrival?.airport?.airportCode?.toUpperCase();
  const filename = `Flight Brief - ${departureCode} ${arrivalCode} - ${departureDate}.pdf`;

  const pdfOptions = useMemo(() => ({
    filename,
    page: {
      margin: 0,
      format: 'A4',
    },
  }), [filename]);

  const { toPDF, targetRef: pdfTargetRef } = usePDF(pdfOptions);

  const triggerDownload = useCallback(async () => {
    try {
      setIsPdfGenerating(true);
      // Petit délai pour laisser le temps au composant PDF de se rendre
      await new Promise((resolve) => { setTimeout(resolve, 500); });
      await toPDF();
      setDownloadAttempted(true);
    } catch (err) {
      // PDF download failed silently
    } finally {
      // Masquer le PDF après génération
      setTimeout(() => {
        setIsPdfGenerating(false);
      }, 500);
    }
  }, [toPDF]);

  // Detect mobile device on component mount
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  // Store temporary download token separately to avoid conflicts with AppContext
  useEffect(() => {
    // Only set token if user is not already authenticated and both params are present
    if (!appState?.auth?.token && token && isDownloadMode) {
      // Store with a different key to avoid AppContext overwriting it
      localStorage.setItem('temp_download_token', token);
    }

    // Cleanup: remove the temporary token when component unmounts
    return () => {
      if (token && isDownloadMode) {
        localStorage.removeItem('temp_download_token');
      }
    };
  }, []);

  // Auto-download PDF when in download mode (both mobile and desktop)
  useEffect(() => {
    if (
      !isDownloadMode
      || !canDownload
      || !flightBrief
      || isLoading
      || autoDownloadStartedRef.current
    ) {
      return undefined;
    }

    autoDownloadStartedRef.current = true;
    const timer = setTimeout(() => {
      triggerDownload();
    }, 1000);

    return () => clearTimeout(timer);
  }, [isDownloadMode, canDownload, flightBrief, isLoading, triggerDownload]);

  const handleManualDownload = () => {
    triggerDownload();
  };

  /**
   * @param {'departure' | 'arrival'} locationParam
   */
  const handleLocationSelection = (locationParam) => {
    setSelectedLocation(locationParam);
  };

  /**
   * Open google maps link
   * @param {string[]} coordinates - The coordinates
   * @returns {void}
   */
  const handleOpenItinerary = (coordinates) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${coordinates.join(',')}`;
    window.open(url, '_blank');
  };

  /**
   * @param {React.RefObject<HTMLDivElement>} containerRef
   * @param {'next' | 'prev'} direction
   */
  const scrollCarousel = (containerRef, direction) => {
    if (containerRef.current) {
      const scrollAmount = 320; // width of item + gap
      // eslint-disable-next-line no-param-reassign
      containerRef.current.scrollLeft += direction === 'next' ? scrollAmount : -scrollAmount;
    }
  };

  const checkScrollPosition = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setIsAtStart(scrollLeft <= 0);
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
    }
  };

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', checkScrollPosition);
      checkScrollPosition(); // Initial check
    }
    return () => {
      if (carousel) {
        carousel.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, []);

  if (isError) {
    return (
      <div className={styles.body}>
        <Box
          direction="row"
          justify="center"
          className={styles.container}
        >
          <div className="margin-m">
            <Message>
              {t(`apiErrors.${error.message}`)}
            </Message>
          </div>
        </Box>
      </div>
    );
  }

  return (
    <div className={styles.body}>
      <Box
        direction="row"
        justify="center"
        className={styles.container}
      >
        <Box
          direction="column"
          gap="l"
          align="center"
          className={styles['left-container']}
        >
          {flightBrief?.displayCompanyLogo ? (
            <Box
              direction="column"
              gap="xs"
              align="center"
              fullWidth
            >
              <LogoFull className={styles.logo} />
            </Box>
          ) : null}
          <Box
            direction="column"
            gap="xs"
            align="flex-start"
            fullWidth
          >
            <h1 className={styles['title-thin']}>
              {t('sharedFlightBrief.title.thin')}
            </h1>
            <h1 className={styles['title-bold']}>
              {t('sharedFlightBrief.title.bold')}
            </h1>
          </Box>

          {/* Download section - only show when download parameter is present and user has permission */}
          {isDownloadMode && canDownload && (
            <Box
              direction="column"
              gap="s"
              align="flex-start"
              fullWidth
              className={styles['download-section']}
            >
              {isMobile && downloadAttempted && (
                <Box direction="row" justify="space-between" align="center" fullWidth>
                  <p className={styles.p1}>
                    {isIOSDevice()
                      ? t('pdfDownload.mobile.downloadedIOS')
                      : t('pdfDownload.mobile.downloadedAndroid')}
                  </p>
                  <Button
                    onClick={handleManualDownload}
                    color="primary"
                  >
                    {t('pdfDownload.mobile.downloadAgain')}
                  </Button>
                </Box>
              )}
              {isMobile && !downloadAttempted && (
                <Box direction="row" justify="space-between" align="center" fullWidth>
                  <Box direction="column" align="center" gap="s">
                    <div className={styles.spinner}>
                      <div className={styles['spinner-dot']} />
                      <div className={styles['spinner-dot']} />
                      <div className={styles['spinner-dot']} />
                    </div>
                    <p className={styles.p1}>
                      {t('pdfDownload.mobile.downloading')}
                    </p>
                  </Box>
                </Box>
              )}
              {!isMobile && downloadAttempted && (
                <Box direction="column" align="center" fullWidth gap="s">
                  <p className={styles.p1}>
                    {t('pdfDownload.desktop.downloaded')}
                  </p>
                  <Button
                    onClick={handleManualDownload}
                    color="primary"
                  >
                    {t('pdfDownload.desktop.downloadAgain')}
                  </Button>
                </Box>
              )}
              {!isMobile && !downloadAttempted && (
                <Box direction="column" align="center" fullWidth gap="s">
                  <p className={styles.p1}>
                    {t('pdfDownload.desktop.downloading')}
                  </p>
                  <div className={styles.spinner}>
                    <div className={styles['spinner-dot']} />
                    <div className={styles['spinner-dot']} />
                    <div className={styles['spinner-dot']} />
                  </div>
                </Box>
              )}
            </Box>
          )}

          {/* flight box */}
          <Box
            direction="column"
            gap="m"
            align="flex-start"
            fullWidth
            className={styles.card}
          >
            <Box
              direction="row"
              justify="space-between"
              fullWidth
              className={styles['card-horizontal-padding']}
            >
              <p className={styles.h5}>{t('sharedFlightBrief.flight.title')?.toUpperCase()}</p>
              <p className={styles['h5-light']}>{formatBookingDateLong(flightBrief?.departure?.date) || '-'}</p>
            </Box>
            <Box
              direction="row"
              justify="space-between"
              align="center"
              fullWidth
              className={`${styles['card-horizontal-padding']} f-nowrap`}
            >
              <Box direction="column" gap="xs" className="flex1">
                <p className={styles.p1}>{capitalizedValue(flightBrief?.departure?.airport?.city) || '-'}</p>
                <p className={styles['h1-bold']}>{flightBrief?.departure?.airport?.airportCode?.toUpperCase() || '-'}</p>
                {flightBrief?.departure?.time ? (
                  <>
                    <p className={styles.p1}>
                      {`${formatTimeString(flightBrief?.departure?.time) || '-'}`}
                    </p>
                    <p className={`${styles['p2-dark']} ws-nowrap`}>
                      {`${flightBrief?.departure?.utc ? t('sharedFlightBrief.flight.utc') : ''} ${flightBrief?.departure?.utc || ''}`}
                    </p>
                  </>
                ) : null}
              </Box>
              <Box
                direction="column"
                gap="xs"
                align="center"
                justify="center"
                className={`${styles.relative} flex1`}
              >
                <Box
                  direction="column"
                  gap="xs"
                  align="center"
                  justify="center"
                >
                  <div className={styles.line} />
                  <div className={styles.tag}>
                    <p>{flightBrief?.flightDuration || '-'}</p>
                  </div>
                </Box>
                {daysBetweenDepartureAndArrival(
                  flightBrief?.departure?.date,
                  flightBrief?.arrival?.date,
                ) ? (
                  <p className={styles['p1-light']}>
                    {t('sharedFlightBrief.flight.dayOffset', {
                      offset: daysBetweenDepartureAndArrival(
                        flightBrief?.departure?.date,
                        flightBrief?.arrival?.date,
                      ),
                    })}
                  </p>
                  ) : null}
              </Box>
              <Box direction="column" align="flex-end" gap="xs" className="flex1">
                <p className={`${styles.p1} text-right`}>{capitalizedValue(flightBrief?.arrival?.airport?.city) || '-'}</p>
                <p className={styles['h1-bold']}>{flightBrief?.arrival?.airport?.airportCode?.toUpperCase() || '-'}</p>
                {flightBrief?.arrival?.time ? (
                  <>
                    <p className={styles.p1}>
                      {`${formatTimeString(flightBrief?.arrival?.time) || '-'}`}
                    </p>
                    <p className={`${styles['p2-dark']} ws-nowrap`}>
                      {`${flightBrief?.arrival?.utc ? t('sharedFlightBrief.flight.utc') : ''} ${flightBrief?.arrival?.utc || ''}`}
                    </p>
                  </>
                ) : null}
              </Box>
            </Box>

            <Box
              direction="row"
              justify="space-between"
              align="center"
              fullWidth
              className={styles['border-top']}
            >
              <Box direction="column" align="flex-start" gap="xs" className="flex1">
                <p className={styles.p1}>{t('sharedFlightBrief.flight.flightNumber')?.toUpperCase()}</p>
                <p className={styles['p1-bold']}>{flightBrief?.flightNumber || '-'}</p>
              </Box>
              <Box direction="row" justify="flex-end" gap="xs" className="flex1">
                <p className={styles.p1}>{t('sharedFlightBrief.flight.passengerNb')?.toUpperCase()}</p>
                <p className={styles['p1-bold']}>
                  {getPassengersTotalCount(flightBrief?.passengers, ['animals']) || '-'}
                </p>
              </Box>
            </Box>

            <Box
              direction="row"
              justify="space-between"
              align="center"
              fullWidth
              className={styles['border-top']}
            >
              <Box direction="column" align="flex-start" gap="xs" className="flex1">
                <p className={styles.p1}>{t('sharedFlightBrief.flight.airplaneType')?.toUpperCase()}</p>
                <p className={styles['p1-bold']}>{flightBrief?.aircraft?.type || '-'}</p>
              </Box>
              <Box direction="column" align="flex-end" gap="xs" className="flex1">
                <p className={styles.p1}>{t('sharedFlightBrief.flight.immat')?.toUpperCase()}</p>
                <p className={styles['p1-bold']}>{flightBrief?.aircraft?.registration || '-'}</p>
              </Box>
            </Box>

            <Box
              direction="row"
              justify="space-between"
              align="center"
              fullWidth
              className={styles['border-top']}
            >
              <Box direction="row" align="flex-start" gap="xs" className="flex1">
                <p className={styles.p1}>{t('sharedFlightBrief.flight.seatNb')?.toUpperCase()}</p>
                <p className={styles['p1-bold']}>{flightBrief?.aircraft?.seatNumber || '-'}</p>
              </Box>
              <Box direction="row" justify="flex-end" gap="xs" className="flex1">
                <p className={styles.p1}>{t('sharedFlightBrief.flight.luggage')?.toUpperCase()}</p>
                <p className={styles['p1-bold']}>{getLuggageTotalCount(flightBrief?.luggage) || '-'}</p>
              </Box>
            </Box>

          </Box>

          {/* location  */}
          <Box direction="column" gap="m" align="flex-start" fullWidth>
            <p className={styles.h3}>{t('sharedFlightBrief.location.title')}</p>
            <Box
              direction="row"
              gap="s"
              align="flex-start"
              fullWidth
            >
              <button
                type="button"
                className={`${styles.button} ${selectedLocation === 'departure' ? styles.selected : ''}`}
                onClick={() => handleLocationSelection('departure')}
              >
                {t('sharedFlightBrief.location.departure')}
              </button>
              <button
                type="button"
                className={`${styles.button} ${selectedLocation === 'arrival' ? styles.selected : ''}`}
                onClick={() => handleLocationSelection('arrival')}
              >
                {t('sharedFlightBrief.location.arrival')}
              </button>
            </Box>
            <Box
              direction="column"
              gap="m"
              align="flex-start"
              fullWidth
              className={styles.card}
            >
              <Box
                direction="row"
                gap="s"
                align="flex-start"
                justify="space-between"
                fullWidth
                className={styles['card-horizontal-padding']}
              >
                <p className={styles['h5-light']}>{flightBrief?.[selectedLocation]?.airport?.airportCode?.toUpperCase() || '-'}</p>
                <div>
                  <p className={styles['h5-light']}>{capitalizedValue(flightBrief?.[`${selectedLocation}Terminal`]?.name) || '-'}</p>
                  <p className={`${styles['p2-dark']} text-right margin-top-xxs`}>{capitalizedValue(flightBrief?.[`${selectedLocation}Terminal`]?.identityCode) || '-'}</p>
                </div>
              </Box>
              <Box
                direction="row"
                gap="s"
                align="flex-start"
                justify="space-between"
                fullWidth
                className={styles['card-horizontal-padding']}
              >
                <div className={`${styles['text-icon']} full-width`}>
                  <Location className={styles['icon-neutral-300']} />
                  <p className={`${styles['p1-light']}`}>
                    {flightBrief?.[`${selectedLocation}Terminal`]?.address || '-'}
                  </p>
                </div>
                <div className={styles['text-icon']}>
                  <Phone className={styles['icon-neutral-300']} />
                  <a href={`tel:${flightBrief?.[`${selectedLocation}Terminal`]?.phone}`} className={styles['p1-light']}>
                    {flightBrief?.[`${selectedLocation}Terminal`]?.phone || '-'}
                  </a>
                </div>
                {flightBrief?.[`${selectedLocation}Terminal`]?.openingTime || flightBrief?.[`${selectedLocation}Terminal`]?.closingTime
                  ? (
                    <div className={styles['text-icon']}>
                      <Clock className={styles['icon-neutral-300']} />
                      <p className={styles['p1-light']}>
                        {formatTerminalOpeningHours(
                          flightBrief?.[`${selectedLocation}Terminal`]?.openingTime,
                          flightBrief?.[`${selectedLocation}Terminal`]?.closingTime,
                        )}
                      </p>
                    </div>
                  ) : null}
              </Box>
              <Box
                direction="row"
                justify="space-between"
                align="center"
                fullWidth
                className={styles['border-top']}
              >
                <Box direction="column" gap="xs">
                  <p className={styles.p1}>{t('sharedFlightBrief.location.meetingTime')?.toUpperCase()}</p>
                  <p className={styles['p1-bold']}>{flightBrief?.[`${selectedLocation}Terminal`]?.meetingInfo}</p>
                </Box>
                {flightBrief?.[`${selectedLocation}Terminal`]?.coordinate?.length === 2
                  && flightBrief?.[`${selectedLocation}Terminal`]?.coordinate?.[0]
                  && flightBrief?.[`${selectedLocation}Terminal`]?.coordinate?.[1]
                  ? (
                    <button
                      type="button"
                      className={`${styles['button-small']} ${styles.selected}`}
                      onClick={() => handleOpenItinerary(flightBrief?.[`${selectedLocation}Terminal`]?.coordinate)}
                    >
                      <span className={styles['btn-text']}>{t('sharedFlightBrief.location.itinerary')}</span>
                      <Itinerary className={styles['btn-icon']} />
                    </button>
                  ) : null}
              </Box>
            </Box>
          </Box>

          {/* extra */}
          <Box direction="column" gap="m" align="flex-start" fullWidth>
            <div className={styles['section-header']}>
              <p className={styles.h3}>{t('sharedFlightBrief.extra.title')}</p>
              <div className={styles['carousel-controls']}>
                <button
                  type="button"
                  className={`${styles['carousel-button']} ${styles.prev}`}
                  onClick={() => scrollCarousel(carouselRef, 'prev')}
                  disabled={isAtStart}
                >
                  <ChevronLeft className={styles['carousel-btn-icon']} />
                </button>
                <button
                  type="button"
                  className={`${styles['carousel-button']} ${styles.next}`}
                  onClick={() => scrollCarousel(carouselRef, 'next')}
                  disabled={isAtEnd}
                >
                  <ChevronRight className={styles['carousel-btn-icon']} />
                </button>
              </div>
            </div>
            <div className={styles['carousel-container']}>
              <div
                ref={carouselRef}
                className={styles.carousel}
              >
                <div
                  className={`${styles.card} ${styles['carousel-item']}`}
                >
                  <p className={styles.p1}>{t('sharedFlightBrief.extra.catering')?.toUpperCase()}</p>
                  <p className={`${styles['p1-light']}`}>{flightBrief?.extra?.catering || '-'}</p>
                </div>
                <div
                  className={`${styles.card} ${styles['carousel-item']}`}
                >
                  <p className={styles.p1}>{t('sharedFlightBrief.extra.transport')?.toUpperCase()}</p>
                  <p className={`${styles['p1-light']}`}>{flightBrief?.extra?.transport || '-'}</p>
                </div>
                <div
                  className={`${styles.card} ${styles['carousel-item']}`}
                >
                  <p className={styles.p1}>{t('sharedFlightBrief.extra.other')?.toUpperCase()}</p>
                  <p className={`${styles['p1-light']}`}>{flightBrief?.extra?.other || '-'}</p>
                </div>
              </div>
            </div>
          </Box>

          {/* passengers */}
          <Box direction="column" gap="m" align="flex-start" fullWidth>
            <p className={styles.h3}>{t('sharedFlightBrief.passengers.title')}</p>
            <Box
              direction="column"
              gap="m"
              align="flex-start"
              fullWidth
              className={styles.card}
            >
              <Box
                direction="row"
                gap="s"
                align="flex-start"
                justify="space-between"
                fullWidth
                className={styles['card-horizontal-padding']}
              >
                <p className={styles['h5-light']}>{t('sharedFlightBrief.passengers.number.title')}</p>
                <p className={styles['h5-light']}>
                  {t(
                    'sharedFlightBrief.passengers.number.value',
                    { count: getPassengersTotalCount(flightBrief?.passengers) },
                  )}
                </p>
              </Box>
              <Box
                direction="row"
                gap="s"
                align="flex-start"
                justify="space-between"
                fullWidth
                className={styles['card-horizontal-padding']}
              >
                <Box
                  direction="row"
                  className={styles['passenger-container']}
                  align="flex-start"
                >
                  <div className={`${styles.tag} ${styles['large-tag']} ${styles['neutral-500-bg']}`}>
                    <Adult />
                    <p className={styles['p1-light']}>{flightBrief?.passengers?.adults || 0}</p>
                  </div>
                  <div className={`${styles.tag} ${styles['large-tag']} ${styles['neutral-500-bg']}`}>
                    <Children />
                    <p className={styles['p1-light']}>{flightBrief?.passengers?.children || 0}</p>
                  </div>
                  <div className={`${styles.tag} ${styles['large-tag']} ${styles['neutral-500-bg']}`}>
                    <Infant />
                    <p className={styles['p1-light']}>{flightBrief?.passengers?.infants || 0}</p>
                  </div>
                  <div className={`${styles.tag} ${styles['large-tag']} ${styles['neutral-500-bg']}`}>
                    <Animal />
                    <p className={styles['p1-light']}>{flightBrief?.passengers?.animals || 0}</p>
                  </div>
                </Box>
                <div className={`${styles.tag} ${styles['large-tag']}`}>
                  <Luggage />
                  <p className={styles['p1-light']}>{getLuggageTotalCount(flightBrief?.luggage) || 0}</p>
                </div>
              </Box>
            </Box>
            {/* other informations */}
            {flightBrief?.information ? (
              <Box direction="column" gap="m" align="flex-start" fullWidth>
                <p className={styles.h3}>{t('sharedFlightBrief.otherInfo.title')}</p>
                <Box
                  direction="column"
                  gap="m"
                  align="flex-start"
                  fullWidth
                  className={`${styles.card} ${styles['card-horizontal-padding']}`}
                >
                  <p className={styles['p1-light']}>{flightBrief?.information}</p>
                </Box>
              </Box>
            ) : null}
            <p className={styles.p2}>
              {t('sharedFlightBrief.passengers.info')}
            </p>
          </Box>
        </Box>
        <div className={styles['right-container']}>
          <RecapPicture className={styles['image-container']} />
        </div>
      </Box>

      {(isDownloadMode && canDownload && isPdfGenerating) && (
      <FlightBriefPDF
        uuid={uuid}
        locale={locale}
        targetRef={pdfTargetRef}
      />
      )}
    </div>
  );
}

export default SharedFlightBrief;
